import { cn } from "@/lib/utils";
import { Badge } from "./badge";

interface LiveBadgeProps {
  isOnline?: boolean;
  isTyping?: boolean;
  unreadCount?: number;
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "dot" | "badge" | "full";
}

export function LiveBadge({ 
  isOnline = false, 
  isTyping = false, 
  unreadCount = 0,
  className,
  size = "md",
  variant = "dot"
}: LiveBadgeProps) {
  const sizes = {
    sm: "w-2 h-2",
    md: "w-3 h-3",
    lg: "w-4 h-4"
  };

  const dotSize = sizes[size];

  if (variant === "dot") {
    return (
      <div className={cn("relative", className)}>
        {isOnline && (
          <div 
            className={cn(
              dotSize,
              "bg-green-500 border-2 border-background rounded-full shadow-sm",
              isTyping ? "animate-pulse bg-blue-500" : "animate-pulse"
            )}
          />
        )}
      </div>
    );
  }

  if (variant === "badge" && unreadCount > 0) {
    const isRecent = unreadCount > 0; // Could be enhanced with timestamp logic
    
    return (
      <Badge 
        variant={isRecent ? "destructive" : "secondary"}
        className={cn(
          "text-xs min-w-5 h-5 px-1.5 transition-all",
          isRecent && "animate-pulse shadow-lg",
          className
        )}
      >
        {unreadCount > 99 ? '99+' : unreadCount}
      </Badge>
    );
  }

  if (variant === "full") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {isOnline && (
          <div className={cn(dotSize, "bg-green-500 rounded-full animate-pulse")} />
        )}
        {isTyping && (
          <span className="text-xs text-blue-600 dark:text-blue-400">typing...</span>
        )}
        {unreadCount > 0 && (
          <Badge variant="destructive" className="text-xs">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </div>
    );
  }

  return null;
}